// lib
import React, { memo, useCallback, useContext, useEffect, useRef, useState } from "react";

// js-utils
import { isEmpty } from "@swiggy-private/js-utils";

// component
import { WidgetControllerContext } from "@swiggy-private/dls-react-mxn";
import ElementObserver from "@swiggy-private/dls-react-mxn/dist/ElementObserver";
import {
    ObjectNames,
    ObjectValues,
    ResponseContext,
    ScreenNames,
    TableContext,
} from "@swiggy-private/portal-dineout-context";
import Chip from "./Chip/Chip";

// constants
import { ACTION_TYPE } from "./constant";

// helpers
import { createAnalyticsContext } from "./helpers";

// styled
import { PillContainer } from "./styled";

// defs
import { IPillsProps } from "./types";
import { isCurrentUserHost } from "../../../utils";
import { SCROLL_TIMEOUT } from "../../constants";
import { scrollToTop } from "../../helper";
import { CustomWidgetTypes } from "../../types";

const MenuPills = ({ tabs, position }: IPillsProps) => {
    const { clickHandler, impressionAnalytics, clickAnalytics } =
        useContext(WidgetControllerContext);

    const { state } = useContext(ResponseContext);
    const { tableId = "", restaurantId = "" } = useContext(TableContext);
    const isMountedRef = useRef(false);
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const [selectedPill, setSelectedPill] = useState<string>("");
    const [isScrolled, setIsScrolled] = useState(false);

    const onPillClick = useCallback(
        (name: string, index: number) => {
            if (isEmpty(clickAnalytics)) return;
            clickAnalytics({
                category: ScreenNames.MENU,
                action: ObjectNames.CLICK_MENU_PILL,
                label: name,
                value: String(index + 1),
                context: createAnalyticsContext({
                    name,
                    tableNo: state?.pin?.data?.tableNo ?? "",
                    pin: state?.pin?.data?.pin ?? "",
                    isUserHost: isCurrentUserHost(state?.pin?.data?.status ?? ""),
                    restaurantId: restaurantId || "",
                    tableId: tableId || "",
                }),
                source: CustomWidgetTypes.MENU_PARENT_CATEGORY_PILLS,
            });
        },
        [
            clickAnalytics,
            tableId,
            restaurantId,
            state?.pin?.data?.pin,
            state?.pin?.data?.status,
            state?.pin?.data?.tableNo,
        ]
    );

    const handlePillClick = useCallback(
        (name: string, index: number) => {
            // analytics
            onPillClick(name, index);

            if (!isEmpty(clickHandler)) {
                clickHandler(undefined, { name, action: ACTION_TYPE });
                setSelectedPill(name);
                // Clear any existing timeout before setting a new one
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }
                scrollTimeoutRef.current = setTimeout(() => {
                    scrollToTop();
                }, SCROLL_TIMEOUT * 2);
            }
        },
        [clickHandler, onPillClick]
    );

    const onWidgetImpression = useCallback(() => {
        if (isEmpty(impressionAnalytics)) return;
        impressionAnalytics({
            category: ScreenNames.MENU,
            action: ObjectNames.IMPRESSION_MENU_PILLS,
            label: ObjectValues.IMPRESSION_MENU_PILLS,
            value: String(position),
            context: "-",
            source: CustomWidgetTypes.MENU_PARENT_CATEGORY_PILLS,
        });
    }, [impressionAnalytics, position]);

    const onPillImpression = useCallback(
        (name: string, index: number) => {
            if (isEmpty(impressionAnalytics)) return;
            impressionAnalytics({
                category: ScreenNames.MENU,
                action: ObjectNames.IMPRESSION_MENU_PILL,
                label: name,
                value: String(index + 1),
                context: createAnalyticsContext({
                    name,
                    tableNo: state?.pin?.data?.tableNo ?? "",
                    pin: state?.pin?.data?.pin ?? "",
                    isUserHost: isCurrentUserHost(state?.pin?.data?.status ?? ""),
                    restaurantId: restaurantId || "",
                    tableId: tableId || "",
                }),
                source: CustomWidgetTypes.MENU_PARENT_CATEGORY_PILLS,
            });
        },
        [
            impressionAnalytics,
            tableId,
            restaurantId,
            state?.pin?.data?.pin,
            state?.pin?.data?.status,
            state?.pin?.data?.tableNo,
        ]
    );

    useEffect(() => {
        isMountedRef.current = true;
        if (!isEmpty(tabs?.[0])) {
            setSelectedPill(tabs?.[0].name || "");
        }

        // Cleanup timeout on unmount
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isEmpty(tabs)) {
        return <></>;
    }

    return (
        <>
            <ElementObserver onViewEnter={onWidgetImpression} senseOnce>
                <>
                    <PillContainer isSticky={true} isVisible={!isScrolled && isMountedRef.current}>
                        {tabs?.map((tab, index) => (
                            <Chip
                                key={tab.name}
                                tab={tab}
                                selectedPill={selectedPill}
                                handlePillClick={(name) => handlePillClick(name, index)}
                            />
                        ))}
                    </PillContainer>

                    <PillContainer isSticky={false} isVisible={true}>
                        {tabs?.map((tab, index) => (
                            <ElementObserver
                                key={tab.name}
                                onViewEnter={() => onPillImpression(tab.name, index)}
                                senseOnce
                            >
                                <Chip
                                    tab={tab}
                                    selectedPill={selectedPill}
                                    handlePillClick={(name) => handlePillClick(name, index)}
                                />
                            </ElementObserver>
                        ))}
                    </PillContainer>
                </>
            </ElementObserver>
            <ElementObserver
                onViewEnter={() => setIsScrolled(true)}
                onViewExit={() => setIsScrolled(false)}
            >
                <div />
            </ElementObserver>
        </>
    );
};

export default memo(MenuPills);
